import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import PageHeader from "@/components/base/PageHeader";
import WorkCard from "@/components/base/WorkCard";
import Pagination from "@/components/base/Pagination";
import type { Work } from "@/types";
import { works as rawWorks } from "@/mocks/works";

const PAGE_SIZE = 8;

const decadeOptions = [
  { label: "全部年代", value: "all" },
  { label: "90 年代", value: "1990" },
  { label: "00 年代", value: "2000" },
  { label: "10 年代", value: "2010" },
  { label: "20 年代", value: "2020" },
];

const typeOptions = [
  { label: "全部类型", value: "all" },
  { label: "电影", value: "电影" },
  { label: "电视剧", value: "电视剧" },
];

const sortOptions = [
  { label: "上映时间（新→旧）", value: "yearDesc" },
  { label: "上映时间（旧→新）", value: "yearAsc" },
  { label: "热门程度", value: "popularity" },
  { label: "评分高低", value: "rating" },
];

export default function Works() {
  useDocumentMeta({
    title: "舒淇影视作品分类 - 电影电视剧",
    description:
      "舒淇影视作品分类页，汇集舒淇主演的电影、电视剧作品，支持按年代、类型筛选及按时间、热度排序，方便影迷快速浏览全部作品。",
    keywords: "舒淇影视作品,舒淇电影,舒淇电视剧,舒淇作品列表",
  });

  const works = rawWorks as unknown as Work[];
  const [searchParams] = useSearchParams();

  const [query, setQuery] = useState(searchParams.get("q") ?? "");
  const [type, setType] = useState(searchParams.get("type") ?? "all");
  const [genre, setGenre] = useState("all");
  const [decade, setDecade] = useState("all");
  const [sort, setSort] = useState("yearDesc");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    const q = searchParams.get("q");
    const t = searchParams.get("type");
    if (q !== null) setQuery(q);
    if (t !== null) setType(t);
    setPage(1);
  }, [searchParams]);

  const genres = useMemo(() => {
    const set = new Set(works.map((w) => w.genre));
    return [{ label: "全部题材", value: "all" }].concat(
      Array.from(set).map((g) => ({ label: g, value: g }))
    );
  }, [works]);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    const result = works.filter((w) => {
      if (type !== "all" && w.type !== type) return false;
      if (genre !== "all" && w.genre !== genre) return false;
      if (decade !== "all") {
        const start = Number(decade);
        if (w.year < start || w.year >= start + 10) return false;
      }
      if (keyword) {
        const haystack = `${w.title} ${w.enTitle} ${w.director} ${w.role} ${w.genre}`.toLowerCase();
        if (!haystack.includes(keyword)) return false;
      }
      return true;
    });

    result.sort((a, b) => {
      switch (sort) {
        case "yearAsc":
          return a.year - b.year;
        case "popularity":
          return b.popularity - a.popularity;
        case "rating":
          return b.rating - a.rating;
        default:
          return b.year - a.year;
      }
    });

    return result;
  }, [works, query, type, genre, decade, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const resetAll = () => {
    setQuery("");
    setType("all");
    setGenre("all");
    setDecade("all");
    setSort("yearDesc");
    setPage(1);
  };

  const applyChange = (fn: () => void) => {
    fn();
    setPage(1);
  };

  const FilterFields = (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-label tracking-widest uppercase text-secondary-500 mb-3">
          类型
        </p>
        <div className="flex flex-wrap gap-2">
          {typeOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => applyChange(() => setType(opt.value))}
              className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                type === opt.value
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-label tracking-widest uppercase text-secondary-500 mb-3">
          题材
        </p>
        <div className="flex flex-wrap gap-2">
          {genres.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => applyChange(() => setGenre(opt.value))}
              className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                genre === opt.value
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-label tracking-widest uppercase text-secondary-500 mb-3">
          年代
        </p>
        <div className="flex flex-wrap gap-2">
          {decadeOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => applyChange(() => setDecade(opt.value))}
              className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                decade === opt.value
                  ? "bg-secondary-700 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader
        eyebrow="Filmography"
        title="舒淇影视作品分类"
        description="汇集舒淇主演的电影与电视剧作品，可按年代、类型、题材筛选，并支持按上映时间与热门程度排序，快速找到你想看的作品。"
      />

      <section className="max-w-content mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-secondary-500" />
            <input
              value={query}
              onChange={(e) => applyChange(() => setQuery(e.target.value))}
              placeholder="搜索作品名称、导演、角色…"
              className="w-full pl-11 pr-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-secondary-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100"
            />
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <select
                value={sort}
                onChange={(e) => applyChange(() => setSort(e.target.value))}
                className="appearance-none pl-4 pr-10 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-accent-400 cursor-pointer"
              >
                {sortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-secondary-500 pointer-events-none" />
            </div>

            <button
              type="button"
              onClick={() => setFilterOpen(true)}
              className="lg:hidden inline-flex items-center gap-2 px-4 py-3 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer"
            >
              <i className="ri-filter-3-line" />
              筛选
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-8">
          <aside className="hidden lg:block lg:col-span-1">
            <div className="sticky top-28 rounded-lg border border-background-200 bg-background-50 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-base font-semibold text-foreground-950">
                  筛选条件
                </h2>
                <button
                  type="button"
                  onClick={resetAll}
                  className="text-xs text-accent-600 hover:text-accent-700 cursor-pointer"
                >
                  重置
                </button>
              </div>
              {FilterFields}
            </div>
          </aside>

          <div className="lg:col-span-3">
            <p className="text-sm text-secondary-500 mb-5">
              共找到 <strong className="text-foreground-950">{filtered.length}</strong> 部作品
            </p>

            {paged.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {paged.map((work) => (
                    <WorkCard key={work.id} work={work} query={query} />
                  ))}
                </div>
                <Pagination
                  page={currentPage}
                  totalPages={totalPages}
                  onChange={setPage}
                />
              </>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-secondary-400">
                  <i className="ri-movie-2-line text-3xl" />
                </div>
                <p className="mt-5 font-heading text-lg text-foreground-950">
                  没有找到符合条件的作品
                </p>
                <p className="mt-2 text-sm text-foreground-600">
                  试试更换关键词或调整筛选条件
                </p>
                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line" />
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {filterOpen && (
        <div className="lg:hidden fixed inset-0 z-[70]">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setFilterOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-background-50 rounded-t-2xl p-6 max-h-[85vh] overflow-y-auto animate-fade-up">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">
                筛选作品
              </h2>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setFilterOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            {FilterFields}
            <div className="mt-8 flex items-center gap-3">
              <button
                type="button"
                onClick={resetAll}
                className="flex-1 px-4 py-3 rounded-md border border-background-300 text-sm text-foreground-700 whitespace-nowrap cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="flex-1 px-4 py-3 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}